import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import './UserManagement.css';
import Button from '../../components/button';
import CustomInput from '../../components/customInput/CustomInput';
import CustomSelect from '../../components/customSelect/CustomSelect';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import EyeIcon from '../../static/image/eyeIcon.svg';
import EditIcon from '../../static/image/editIcon.svg';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import CreateUser from '../createUser/CreateUser';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, deleteUser, fetchUserDetails } from '../../redux/userSlice';
import { fetchRoles } from '../../redux/createUserSlice';
import {
  EDIT_MODEL_CREATE,
  EDIT_MODEL_EDIT,
  EDIT_MODEL_VIEW,
  PERMISSION_VALUES,
  MENU_IDS,
  PERMISSION_CONSTANTS,
  ROUTES
} from '../../utils/constant';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { formatDatetime } from '../../utils/dateUtils';
import { decodedJWTPayload } from '../../utils/commonUtil';

function UserManagement() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const location = useLocation();

  const [inputValue1, setInputValue1] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10
  });

  const getEditModeFromRoute = () => {
    if (location.pathname.includes('/create')) return EDIT_MODEL_CREATE;
    if (location.pathname.includes('/edit/')) return EDIT_MODEL_EDIT;
    if (location.pathname.includes('/view/')) return EDIT_MODEL_VIEW;
    return undefined;
  };

  const [editMode, setEditMode] = useState(getEditModeFromRoute());
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [permission, setPermission] = useState('');

  const dispatch = useDispatch();
  const { data, loading, pagination: reduxPagination } = useSelector((state) => state.users);
  const { roles } = useSelector((state) => state.createUser);

  useEffect(() => {
    dispatch(fetchUsers(pagination));
    dispatch(fetchRoles());
    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      const mnId = payload?.mn_id;

      if (mnId) {
        const jwtPattern = PERMISSION_CONSTANTS.CHECKS.getJwtPattern(MENU_IDS.USER_MANAGEMENT);
        const match = mnId.match(jwtPattern);
        if (match) {
          setPermission(match[1]);
        }
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const newEditMode = getEditModeFromRoute();
    setEditMode(newEditMode);
    if ((newEditMode === EDIT_MODEL_EDIT || newEditMode === EDIT_MODEL_VIEW) && userId) {
      handleFetchUserDetails(userId);
    } else if (newEditMode === EDIT_MODEL_CREATE) {
      setSelectedUser(null);
    }
  }, [location.pathname, userId]);

  const allRoleOptions = useMemo(() => {
    return roles.map((role) => ({
      value: role.roleId.toString(),
      label: role.roleName
    }));
  }, [roles]);

  const columns = [
    {
      name: 'Username',
      cell: (row) => <div>{row.login}</div>
    },
    {
      name: 'Role',
      cell: (row) => <div>{row.roleName}</div>
    },
    {
      name: 'Status',
      cell: (row) => <div>{row.isActive ? 'Active' : 'In-Active'}</div>
    },
    {
      name: 'Created By',
      cell: (row) => <div>{row.createdBy}</div>
    },
    {
      name: 'Created Date/Time',
      cell: (row) => <div>{formatDatetime(row.createdDatetime)}</div>
    },
    {
      name: 'Updated By',
      cell: (row) => <div>{row.updatedBy}</div>
    },
    {
      name: 'Updated Date/Time',
      cell: (row) => <div>{formatDatetime(row.updatedDatetime)}</div>
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <img src={EyeIcon} alt="Eye" onClick={() => handleViewUser(row.uuid)} style={{ cursor: 'pointer' }} />
          <img
            src={EditIcon}
            alt="Edit"
            onClick={() => handleEditUser(row.uuid)}
            style={{ opacity: permission.includes(PERMISSION_VALUES.WRITE) ? 1 : 0.5, cursor: permission.includes(PERMISSION_VALUES.WRITE) ? 'pointer' : 'default' }}
          />
          <img
            src={DeleteIcon}
            alt="Delete"
            onClick={() => handleDeleteClick(row)}
            style={{ opacity: permission === PERMISSION_VALUES.FULL_ACCESS ? 1 : 0.5, pointerEvents: permission === PERMISSION_VALUES.FULL_ACCESS ? 'auto' : 'none', cursor: permission === PERMISSION_VALUES.FULL_ACCESS ? 'pointer' : 'default' }}
          />
        </div>
      ),
      sortable: false
    }
  ];

  const handleFetchUserDetails = async (userId) => {
    try {
      const userDetails = await dispatch(fetchUserDetails(userId)).unwrap();
      setSelectedUser(userDetails.resdata);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      navigate(ROUTES.USER_MANAGEMENT);
    }
  };

  const handlePageChange = (page) => {
    const newPagination = { ...pagination, page: page - 1 };
    setPagination(newPagination);
    dispatch(fetchUsers({
      ...newPagination,
      login: inputValue1,
      roleId: selectedRoleId
    }));
  };

  const handlePerRowsChange = (newPerPage, page) => {
    const newPagination = { page: page - 1, size: newPerPage };
    setPagination(newPagination);
    dispatch(fetchUsers({
      ...newPagination,
      login: inputValue1,
      roleId: selectedRoleId
    }));
  };

  const handleCreateUser = () => {
    if (permission.includes(PERMISSION_VALUES.WRITE)) {
      navigate(ROUTES.USER_CREATE);
    }
  };

  const handleDeleteClick = (user) => {
    if (permission === PERMISSION_VALUES.FULL_ACCESS) {
      setSelectedUser(user);
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = () => {
    dispatch(deleteUser(selectedUser.uuid))
      .then(() => {
        dispatch(fetchUsers({
          ...pagination,
          login: inputValue1,
          roleId: selectedRoleId
        }));
        setShowDeleteModal(false);
        if (editMode) {
          navigate(ROUTES.USER_MANAGEMENT);
        }
      })
      .catch((error) => {
        console.error(error);
        setShowDeleteModal(false);
      });
  };

  const handleSearch = () => {
    const newPagination = { ...pagination, page: 0 };
    setPagination(newPagination);
    dispatch(fetchUsers({
      ...newPagination,
      login: inputValue1,
      roleId: selectedRoleId
    }));
    if (editMode) {
      navigate(ROUTES.USER_MANAGEMENT);
    }
  };

  const handleViewUser = (userId) => {
    navigate(`/dashboard/user-management/view/${userId}`);
  };

  const handleEditUser = (userId) => {
    if (permission.includes(PERMISSION_VALUES.WRITE)) {
      navigate(`/dashboard/user-management/edit/${userId}`);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.USER_MANAGEMENT);
  };

  const handleReset = () => {
    setInputValue1('');
    setSelectedRoleId('');
    const newPagination = { ...pagination, page: 0 };
    setPagination(newPagination);
    dispatch(fetchUsers(newPagination));
    if (editMode) {
      navigate(ROUTES.USER_MANAGEMENT);
    }
  };

  const handleUserCreated = () => {
    dispatch(fetchUsers(pagination));
    navigate(ROUTES.USER_MANAGEMENT);
  };

  const handleUserUpdated = () => {
    dispatch(fetchUsers(pagination));
    navigate(ROUTES.USER_MANAGEMENT);
  };

  return (
    <>
      {!editMode && (
        <div className="manage-user-container">
          <div style={{ padding: '30px' }}>
            <div>Manage User</div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '30px', gap: '20px', flexWrap: 'wrap' }}>
              <Button
                className="manage-user-btn"
                onClick={handleCreateUser}
                style={{ opacity: permission.includes(PERMISSION_VALUES.WRITE) ? 1 : 0.5, pointerEvents: permission.includes(PERMISSION_VALUES.WRITE) ? 'auto' : 'none', cursor: permission.includes(PERMISSION_VALUES.WRITE) ? 'pointer' : 'default' }}
              >
                CREATE USER
              </Button>
              <div>
                <CustomInput
                  placeholder="Username"
                  style={{ width: '240px', height: '45px' }}
                  value={inputValue1}
                  onChange={(e) => setInputValue1(e.target.value)}
                />
              </div>
              <div>
                <CustomSelect
                  placeholder="Role Name"
                  option={allRoleOptions}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  selected={selectedRoleId}
                  style={{ width: '240px', height: '45px' }}
                />
              </div>
              <Button className="manage-user-btn" onClick={handleSearch} style={{ cursor: 'pointer' }}>
                SEARCH
              </Button>
              <Button className="manage-user-btn" onClick={handleReset} style={{ cursor: 'pointer' }}>
                RESET
              </Button>
            </div>
            <div style={{ marginTop: '20px' }}>
              <CustomPaginationTable
                data={data}
                totalRows={reduxPagination?.total || 0}
                loading={loading}
                perPage={pagination.size}
                currentPage={pagination.page + 1}
                onPageChange={handlePageChange}
                columns={columns}
                onPerRowsChange={handlePerRowsChange}
                isHiddenPag={false}
              />
            </div>
          </div>
        </div>
      )}
      {editMode && (
        <CreateUser
          editMode={editMode}
          user={selectedUser}
          onCancel={handleCancel}
          onUserCreated={handleUserCreated}
          onUserUpdated={handleUserUpdated}
        />
      )}
      <CommonDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

export default UserManagement;